import React, { useEffect, useRef } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useModal } from "../context/ModalContext";
import { servicesData, productsData } from "../data/languageData";

const Modal = () => {
  const { translate, currentLanguage } = useLanguage();
  const { modalOpen, modalContent, modalType, closeModal } = useModal();
  const modalRef = useRef();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    const handleClickOutside = (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        e.target.id === "modalOverlay"
      ) {
        closeModal();
      }
    };

    if (modalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [modalOpen, closeModal]);

  if (!modalOpen) return null;

  const renderLearnMoreModal = () => (
    <div ref={modalRef} className="modal">
      <div className="modal-header">
        <h3>
          <i className="fas fa-info-circle"></i> {translate("learn-more")}
        </h3>
        <button className="close-modal" onClick={closeModal}>
          &times;
        </button>
      </div>
      <div className="modal-content">
        <p>{translate("hero-desc")}</p>

        <h4>{translate("services-label")}:</h4>
        <div className="modal-features">
          {Object.keys(servicesData).map((serviceId, index) => (
            <div className="feature-item" key={serviceId}>
              <div className="feature-icon">
                <i className={servicesData[serviceId].icon}></i>
              </div>
              <div>
                <h5>{translate(servicesData[serviceId].title)}</h5>
                <p>{translate(servicesData[serviceId].description)}</p>
              </div>
            </div>
          ))}
        </div>

        <h4>Produk Kami:</h4>
        <div className="modal-features">
          {Object.keys(productsData).map((productId) => (
            <div className="feature-item" key={productId}>
              <div
                className="feature-icon"
                style={{ background: "var(--gradient-teal)" }}
              >
                <i className={productsData[productId].icon}></i>
              </div>
              <div>
                <h5>{productsData[productId].title[currentLanguage]}</h5>
                <p>{productsData[productId].description[currentLanguage]}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-cta">
          <h4>
            {currentLanguage === "id" ? "Mari Berdiskusi" : "Let's Discuss"}
          </h4>
          <p>
            {currentLanguage === "id"
              ? "Kami siap membantu Anda mencapai tujuan digital bisnis Anda. Hubungi kami untuk konsultasi gratis atau demo produk."
              : "We are ready to help you achieve your business digital goals. Contact us for free consultation or product demo."}
          </p>

          <div className="modal-buttons">
            <button
              className="learn-more-btn"
              onClick={() => {
                closeModal();
                const contactElement = document.getElementById("contact");
                if (contactElement) {
                  window.scrollTo({
                    top: contactElement.offsetTop - 80,
                    behavior: "smooth",
                  });
                }
              }}
            >
              <i className="fas fa-envelope"></i> {translate("contact-us")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderServiceModal = () => {
    if (!modalContent) return null;

    if (modalContent.title === "modal-website-title") {
      return (
        <div ref={modalRef} className="modal">
          <div className="modal-header">
            <h3>
              <i className={modalContent.icon}></i>{" "}
              {translate(modalContent.title)}
            </h3>
            <button className="close-modal" onClick={closeModal}>
              &times;
            </button>
          </div>
          <div className="modal-content">
            <p>{translate(modalContent.description)}</p>

            <h4>{translate("modal-features")}</h4>
            <div className="modal-features">
              {modalContent.features.map((feature, index) => (
                <div className="feature-item" key={index}>
                  <div className="feature-icon">
                    <i className="fas fa-check"></i>
                  </div>
                  <div>
                    <p>{feature[currentLanguage]}</p>
                  </div>
                </div>
              ))}
            </div>

            <h4>{translate("modal-packages")}</h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "20px",
                margin: "25px 0",
              }}
            >
              {modalContent.packages.map((pkg, index) => (
                <div
                  key={index}
                  style={{
                    background: "var(--light-blue)",
                    borderRadius: "10px",
                    padding: "20px",
                    border: "1px solid rgba(26, 107, 255, 0.2)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <h5 style={{ margin: "0", color: "var(--dark-blue)" }}>
                      {translate(pkg.name)}
                    </h5>
                    <span
                      style={{
                        background: "var(--gradient-blue)",
                        color: "white",
                        padding: "5px 15px",
                        borderRadius: "20px",
                        fontWeight: "600",
                      }}
                    >
                      {pkg.price}
                    </span>
                  </div>
                  <p style={{ margin: "0", fontSize: "0.9rem", color: "#666" }}>
                    {translate(pkg.features)}
                  </p>
                </div>
              ))}
            </div>

            <div className="modal-cta">
              <h4>{translate("modal-details")}</h4>
              <p>
                <strong>{translate("modal-timeline")}</strong>{" "}
                {modalContent.timeline[currentLanguage]}
              </p>

              <div className="modal-buttons">
                <button
                  className="learn-more-btn"
                  onClick={() => {
                    window.open(
                      `https://wa.me/62123456789?text=${encodeURIComponent(translate(modalContent.title))}`,
                    );
                  }}
                >
                  <i className="fab fa-whatsapp"></i>{" "}
                  {translate("modal-whatsapp")}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div ref={modalRef} className="modal">
        <div className="modal-header">
          <h3>
            <i className={modalContent.icon}></i>{" "}
            {translate(modalContent.title)}
          </h3>
          <button className="close-modal" onClick={closeModal}>
            &times;
          </button>
        </div>
        <div className="modal-content">
          <p>{translate(modalContent.description)}</p>

          <h4>{translate("modal-features")}</h4>
          <div className="modal-features">
            {modalContent.features.map((feature, index) => (
              <div className="feature-item" key={index}>
                <div className="feature-icon">
                  <i className="fas fa-check"></i>
                </div>
                <div>
                  <p>{feature[currentLanguage]}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-cta">
            <h4>{translate("modal-details")}</h4>
            <p>
              <strong>{translate("modal-pricing")}</strong>{" "}
              {modalContent.pricing[currentLanguage]}
            </p>
            <p>
              <strong>{translate("modal-timeline")}</strong>{" "}
              {modalContent.timeline[currentLanguage]}
            </p>

            <div className="modal-buttons">
              <button
                className="learn-more-btn"
                onClick={() => {
                  window.open(
                    `https://wa.me/62123456789?text=${encodeURIComponent(translate(modalContent.title))}`,
                  );
                }}
              >
                <i className="fab fa-whatsapp"></i>{" "}
                {translate("modal-whatsapp")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProductModal = () => {
    if (!modalContent) return null;

    return (
      <div ref={modalRef} className="modal">
        <div className="modal-header">
          <h3>
            <i className={modalContent.icon}></i>{" "}
            {modalContent.title[currentLanguage]}
          </h3>
          <button className="close-modal" onClick={closeModal}>
            &times;
          </button>
        </div>
        <div className="modal-content">
          <p>{modalContent.description[currentLanguage]}</p>

          <h4>{translate("modal-saas-features")}</h4>
          <div className="modal-features">
            {modalContent.features.map((feature, index) => (
              <div className="feature-item" key={index}>
                <div className="feature-icon">
                  <i className="fas fa-check"></i>
                </div>
                <div>
                  <p>{feature[currentLanguage]}</p>
                </div>
              </div>
            ))}
          </div>

          <h4>{translate("modal-benefits")}</h4>
          <div className="modal-features">
            {modalContent.benefits.map((benefit, index) => (
              <div className="feature-item" key={index}>
                <div
                  className="feature-icon"
                  style={{
                    background:
                      "linear-gradient(135deg, #00c9b7 0%, #009688 100%)",
                  }}
                >
                  <i className="fas fa-chart-line"></i>
                </div>
                <div>
                  <p>{benefit[currentLanguage]}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-cta">
            <h4>
              {translate("modal-start-using")}{" "}
              {modalContent.title[currentLanguage].split(" - ")[0]}:
            </h4>
            <p>
              <strong>{translate("modal-subscription")}</strong>{" "}
              {modalContent.pricing[currentLanguage]}
            </p>
            <p>
              <strong>{translate("modal-free-trial")}</strong>{" "}
              {modalContent.trial[currentLanguage]}
            </p>

            <div className="modal-buttons">
              <button
                className="trial-btn"
                onClick={() => {
                  alert(
                    currentLanguage === "id"
                      ? "Untuk mencoba free trial, silahkan hubungi kami di info@sakaka.id atau klik tombol Hubungi Kami di header"
                      : "To try free trial, please contact us at info@sakaka.id or click the Contact Us button in the header",
                  );
                }}
              >
                <i className="fas fa-play-circle"></i>{" "}
                {translate("modal-trial-btn")}
              </button>
              <button
                className="demo-btn"
                onClick={() => {
                  closeModal();
                  const contactElement = document.getElementById("contact");
                  if (contactElement) {
                    window.scrollTo({
                      top: contactElement.offsetTop - 80,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                <i className="fas fa-calendar"></i>{" "}
                {translate("modal-demo-btn")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderModalContent = () => {
    switch (modalType) {
      case "learn-more":
        return renderLearnMoreModal();
      case "service":
        return renderServiceModal();
      case "product":
        return renderProductModal();
      default:
        return null;
    }
  };

  return (
    <div
      className={`modal-overlay ${modalOpen ? "active" : ""}`}
      id="modalOverlay"
    >
      {renderModalContent()}
    </div>
  );
};

export default Modal;
